import { AGREEMENT_STATUSES } from "@equidefi/constants/agreement";
import { SigningPartyStatus } from "@equidefi/constants/agreements";
import type { DTO } from "@equidefi/shared/types/dto";
import { Icon } from "@equidefi/ui/icon";
import type React from "react";

import { VAULT_ROUTES } from "@/constants/routes";

import { InvestmentStepCard } from "./InvestmentStepCard";

interface Signatures {
  investment_agreement?: { status?: string };
  is_countersigned?: boolean;
  investors?: Array<{ status: string }>;
}

interface AgreementStepCardProps {
  investment: DTO.Investment;
  signatures: Signatures | undefined;
}

export const AgreementStepCard: React.FC<AgreementStepCardProps> = ({ investment, signatures }) => {
  const to = VAULT_ROUTES.agreements(investment.id);

  if (signatures?.is_countersigned) {
    return (
      <InvestmentStepCard
        scheme="green"
        label="COMPLETE"
        heading="Agreement Countersigned"
        text="Your subscription agreement is now available for download."
        icon={Icon.Edit3}
        to={to}
      />
    );
  }

  const agreementStatus = signatures?.investment_agreement?.status;

  if (agreementStatus === AGREEMENT_STATUSES.VOIDED || agreementStatus === AGREEMENT_STATUSES.CANCELED) {
    return (
      <InvestmentStepCard
        scheme="red"
        label="CANCELLED"
        heading="Agreement Cancelled"
        text="Your subscription agreement is no longer active."
        icon={Icon.Edit3}
        to={to}
      />
    );
  }

  const investors = signatures?.investors ?? [];

  if (investors.some((i) => i.status === SigningPartyStatus.DECLINED)) {
    return (
      <InvestmentStepCard
        scheme="red"
        label="DECLINED"
        heading="Agreement Declined"
        text="A signer declined to sign the subscription agreement."
        icon={Icon.Edit3}
        to={to}
      />
    );
  }

  const allSigned = investors.length > 0 && investors.every((i) => i.status === SigningPartyStatus.SIGNED);

  if (allSigned) {
    return (
      <InvestmentStepCard
        scheme="blue"
        label="IN REVIEW"
        heading="Awaiting Countersignature"
        text="All signers have signed. The issuer will countersign the agreement."
        icon={Icon.Edit3}
        to={to}
      />
    );
  }

  return (
    <InvestmentStepCard
      scheme="yellow"
      label="PENDING"
      heading="Signatures Pending"
      text="All signers must sign the subscription agreement."
      icon={Icon.Edit3}
      to={to}
    />
  );
};

export default AgreementStepCard;
